// The reporting half of the dashboard — recharter, advancement, committee
// health, membership and leader training, aggregated from the Unit Health
// import data. Sits below the Strategic Plan tiles + Needs Attention.
//
// Layout: alert strip + district-comparison heatmap run full width; the
// supporting sections sit in a dense two-column grid; membership is full
// width for the trend. Exception-first — amber/red counts carry the weight,
// healthy green recedes. No calculation / data / scoping changes here.

import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../components/Layout';
import { useDistrictSummaries, useVisibleMembership, useVisibleUnits } from '../../hooks/useUnits';
import { useAffiliatedHolders, useVisibleContacts } from '../../hooks/useScopedData';
import { hasCouncilDashboardAccess, hasCouncilFullRead } from '../../lib/visibility';
import InfoTip from '../../components/InfoTip';
import Spinner from '../../components/Spinner';
import SeasonalSalePanel from './SeasonalSalePanel';
import {
  CHART_COLORS,
  Donut,
  ChartLegend,
  ExceptionBars,
  LineChart,
  type ExRow,
} from '../../components/Charts';
import {
  advancementPace,
  advancementPaceByDistrict,
  advancementYtd,
  alertCounts,
  charterByDistrict,
  charterCounts,
  committeeHealth,
  heatmapRows,
  membershipSeries,
  sytStats,
  trainedStats,
  weatherStats,
  type AdvancementPace,
  type CharterCounts,
} from '../../lib/dashboardMetrics';
import { localTodayIso, renewalProgress, type RenewalProgress } from '../../lib/charterRenewal';
import type { LeaderFilter, UnitFilter } from '../../lib/unitHealth';
import { DEFAULT_DASHBOARD_THRESHOLDS, recName } from '../../types';

/** Where a dashboard drill-down should land in Unit Health. */
interface Drill {
  district?: string;
  filter?: UnitFilter | LeaderFilter;
  view?: 'units' | 'leaders';
}
type OnDrill = (d?: Drill) => void;

export default function HealthSections() {
  const { profile, settings, districtFilter, setDistrictFilter, districts } = useAppContext();
  const navigate = useNavigate();

  const admin = profile.role === 'admin';
  // A plain non-admin's "All" is really just an unmovable default (no
  // picker to choose it) — falls back to their own district. Admin and
  // Council Access (Dashboard Only and up — this safe aggregate is exactly
  // what that tier grants) have a real picker, so their "All" is a genuine
  // choice and means the true council-wide aggregate (scope: null).
  const unscoped = admin || hasCouncilDashboardAccess(profile);
  const scope = districtFilter === 'All' ? (unscoped ? null : (profile.districtId ?? null)) : districtFilter;
  // Drill-down into Unit Health/Org Chart detail stays gated to Full
  // Read/RW (or admin, or simply having a home district) — Dashboard Only
  // sees the aggregate above but must not click through into module
  // detail its own queries would come back denied for.
  const canDrillIntoModules = admin || hasCouncilFullRead(profile) || !!profile.districtId;
  const showHeatmap = admin || districts.length > 1;
  const t = settings.dashboardThresholds ?? DEFAULT_DASHBOARD_THRESHOLDS;

  const summariesQ = useDistrictSummaries(profile);
  const contactsQ = useVisibleContacts(profile);
  const holdersQ = useAffiliatedHolders(profile);
  const membershipQ = useVisibleMembership(profile);
  // Unit-level charter dates drive the annual renewal progress. Readers without unit access (Dashboard Only) get none, and
  // keep the summary-based Charter renewal card below.
  const unitsQ = useVisibleUnits(profile);

  const summaries = summariesQ.data;
  const loading = summariesQ.loading && summaries.length === 0;
  const noData = !summariesQ.loading && summaries.length === 0;
  const scopeName = scope ? recName(settings.districts, scope) : 'Council';

  // ---- section data — health metrics come from districtSummaries ----
  const alerts = useMemo(() => alertCounts(summaries, scope), [summaries, scope]);
  const heat = useMemo(
    () => heatmapRows(summaries, settings.districts, t),
    [summaries, settings.districts, t],
  );
  const charter = useMemo(() => charterCounts(summaries, scope), [summaries, scope]);
  const charterBars = useMemo(
    () => charterByDistrict(summaries, settings.districts),
    [summaries, settings.districts],
  );
  const renewal = useMemo(
    () => renewalProgress(unitsQ.data, settings.districts, localTodayIso()),
    [unitsQ.data, settings.districts],
  );
  const ytd = useMemo(() => advancementYtd(summaries, scope), [summaries, scope]);
  const pace = useMemo(() => advancementPace(summaries, scope), [summaries, scope]);
  const paceBars = useMemo(
    () => advancementPaceByDistrict(summaries, settings.districts),
    [summaries, settings.districts],
  );
  const committees = useMemo(
    () =>
      committeeHealth(
        contactsQ.data,
        settings.positions,
        settings.committees,
        settings.districts,
        scope,
        t.committeeFlagPct,
        holdersQ.holders,
      ),
    [contactsQ.data, holdersQ.holders, settings.positions, settings.committees, settings.districts, scope, t],
  );
  const memberSeries = useMemo(
    () => membershipSeries(membershipQ.data, scope),
    [membershipQ.data, scope],
  );
  const syt = useMemo(() => sytStats(summaries, scope), [summaries, scope]);
  const trained = useMemo(() => trainedStats(summaries, scope), [summaries, scope]);
  const weather = useMemo(() => weatherStats(summaries, scope), [summaries, scope]);

  const drill: OnDrill = (d = {}) => {
    if (!canDrillIntoModules) return;
    if (d.district) setDistrictFilter(d.district);
    navigate('/units', d.filter || d.view ? { state: { uhFilter: d.filter, uhView: d.view ?? 'units' } } : undefined);
  };
  const drillOrg = (district?: string) => {
    if (!canDrillIntoModules) return;
    if (district) setDistrictFilter(district);
    navigate('/org');
  };

  if (loading) {
    return (
      <section className="card dash-card">
        <div style={{ padding: '4px 0' }}>
          <Spinner label="Loading district health…" />
        </div>
      </section>
    );
  }
  if (noData) {
    return (
      <section className="card dash-card">
        <header className="dash-card-head">
          <h3>District heads-up</h3>
        </header>
        <div className="dash-empty">
          No unit health data yet — import the reports on Unit Health to light this up.
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Alert strip — top-level triage */}
      <div className="dash-alert" role="group" aria-label="Attention needed">
        <AlertChip
          n={alerts.recharterOverdue}
          label={<>Units overdue<br />recharter</>}
          tone="bad"
          icon={ICONS.alert}
          onClick={() => drill({ filter: 'recharterOverdue' })}
        />
        <AlertChip
          n={alerts.recharterSoon}
          label={<>Units due within<br />60 days</>}
          tone="warn"
          icon={ICONS.clock}
          onClick={() => drill({ filter: 'recharterDue60' })}
        />
        <AlertChip
          n={alerts.leadershipGaps}
          label="Leadership gaps"
          tone="warn"
          icon={ICONS.people}
          onClick={() => drill({ filter: 'leadershipGap' })}
        />
        <AlertChip
          n={alerts.sytFlags}
          label="SYT flags"
          tone="bad"
          icon={ICONS.shield}
          onClick={() => drill({ filter: 'sytNotCurrent', view: 'leaders' })}
        />
        <span className="dash-alert-scope">· {scopeName}</span>
      </div>

      {/* District comparison — the centrepiece. Design preserved. */}
      {showHeatmap && (
        <section className="card dash-card">
          <header className="dash-card-head">
            <div>
              <h3>District comparison</h3>
              <p className="dash-card-sub">Where to look first this week. Green is on target.</p>
            </div>
            <span className="dash-card-hint">Click a district to filter</span>
          </header>
          <div className="dash-heat-wrap">
            <table className="dash-heat">
              <thead>
                <tr>
                  <th>District</th>
                  <th>
                    Charter Current
                    <InfoTip label="What “Charter Current” means">
                      <b>Charter Current</b> is the share of units whose charter is <b>still
                      valid today</b>: the expiration date has not passed.
                      <br />
                      <br />
                      It counts a unit as current regardless of upcoming renewal deadlines, until
                      its actual expiration date passes. Renewal progress is tracked in the Charter
                      renewal section below.
                    </InfoTip>
                  </th>
                  <th>SYT current</th>
                  <th>Trained</th>
                  <th>
                    Stalled units
                    <InfoTip label="What “Stalled units” means">
                      A <b>Stalled</b> unit is a Pack or Troop with registered youth that
                      has recorded <b>zero rank advancements this program year</b>, per the
                      latest advancement report.
                      <br />
                      <br />
                      Crews, Ships, Posts and Clubs aren’t counted (no rank ladder), and a
                      unit with no report imported isn’t counted either.
                    </InfoTip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {heat.map((row) => (
                  <tr key={row.districtId} className="dash-heat-row" onClick={() => drill({ district: row.districtId })}>
                    <td>
                      <span className="dash-heat-name">{row.name}</span>
                    </td>
                    <td className={`dash-heat-${row.recharter.tone}`}>{row.recharter.display}</td>
                    <td className={`dash-heat-${row.syt.tone}`}>{row.syt.display}</td>
                    <td className={`dash-heat-${row.trained.tone}`}>{row.trained.display}</td>
                    <td
                      className={`dash-heat-${row.stalled.tone}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        drill({ district: row.districtId, filter: 'stalled' });
                      }}
                    >
                      {row.stalled.display}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Two-column supporting grid — split into three rows so the Seasonal Sale
          row can sit between Charter/Advancement and Committee/Leader without
          disturbing either existing pair. */}
      <div className="dash-grid">
        {unitsQ.data.length > 0 ? (
          <RenewalCard scope={scope} progress={renewal} onDrill={drill} />
        ) : unitsQ.loading ? (
          <CardShell title="Charter Renewal">
            <Spinner />
          </CardShell>
        ) : (
          <CharterCard scope={scope} scopeName={scopeName} counts={charter} bars={charterBars} onDrill={drill} />
        )}
        <AdvancementCard scope={scope} scopeName={scopeName} ytd={ytd} pace={pace} bars={paceBars} onDrill={drill} />
      </div>

      {/* The Friends of Scouting summary panel returns with the new FOS module (confirmed totals, goals and
          progress by authorized scope). The retired prototype panel read data that no longer exists. */}
      <div className="dash-grid">
        <SeasonalSalePanel />
      </div>

      <div className="dash-grid">
        <CommitteeCard scope={scope} committees={committees} flagPct={t.committeeFlagPct} onDrill={drillOrg} />
        <LeaderCard
          scope={scope}
          scopeName={scopeName}
          syt={syt}
          trained={trained}
          weather={weather}
          alerts={alerts}
          onDrill={drill}
        />
      </div>

      {/* Membership — full width for the trend */}
      <MembershipCard scope={scope} scopeName={scopeName} series={memberSeries} />
    </>
  );
}

// ================================================================
// Cards
// ================================================================

function CardShell({
  title,
  sub,
  link,
  children,
}: {
  title: string;
  sub?: string;
  link?: { label: string; onClick: () => void };
  children: ReactNode;
}) {
  return (
    <section className="card dash-card">
      <header className="dash-card-head">
        <div>
          <h3>{title}</h3>
          {sub && <p className="dash-card-sub">{sub}</p>}
        </div>
        {link && (
          <button className="dash-card-link" onClick={link.onClick}>
            {link.label} →
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function charterException(c: {
  chartered: number;
  soon: number;
  overdue: number;
}): ExRow['exception'] {
  if (c.overdue > 0) return { text: `${c.overdue} overdue`, tone: 'bad' };
  if (c.soon > 0) return { text: `${c.soon} due ≤ 60d`, tone: 'warn' };
  return null;
}

/** Annual renewal progress: one bar per district (renewed / pending / overdue sum to the district's whole renewal
 *  population), with the renewed count and percentage up front. */
function RenewalCard({
  scope,
  progress,
  onDrill,
}: {
  scope: string | null;
  progress: RenewalProgress;
  onDrill: OnDrill;
}) {
  const rows = scope ? progress.rows.filter((r) => r.districtId === scope) : progress.rows;
  const shown = scope ? rows[0] : progress.totals;
  return (
    <CardShell
      title={`Charter Renewal — ${progress.year}`}
      sub={`Units renewed for the ${progress.year} charter year`}
      link={{ label: 'Unit Health', onClick: () => onDrill() }}
    >
      {!shown || shown.total === 0 ? (
        <p className="dash-empty">No recharter report imported yet.</p>
      ) : (
        <>
          <div className="renew-hero">
            <span className="renew-hero-pct">{shown.pct}%</span>
            <span className="renew-hero-text">
              <b>{shown.renewed}</b> of {shown.total} units renewed
            </span>
          </div>
          <div className="exbars">
            {rows.map((r) => (
              <button
                key={r.districtId}
                className="exbar exbar-click exbar-renew"
                onClick={() => onDrill({ district: r.districtId })}
              >
                <span className="exbar-label">{r.name}</span>
                <span className="exbar-track" aria-hidden>
                  {[
                    { label: 'renewed', value: r.renewed, color: CHART_COLORS.good },
                    { label: 'pending', value: r.pending, color: CHART_COLORS.warn },
                    { label: 'overdue', value: r.overdue, color: CHART_COLORS.bad },
                  ]
                    .filter((seg) => seg.value > 0)
                    .map((seg) => (
                      <span key={seg.label} className="exbar-seg" style={{ flexGrow: seg.value, background: seg.color }} />
                    ))}
                </span>
                <span className="exbar-note renew-note">
                  <span className="renew-main">
                    {r.renewed} of {r.total} renewed ({r.pct}%)
                  </span>
                  <span className="renew-sub">
                    {r.pending} pending
                    {r.overdue > 0 && <span className="renew-overdue"> · {r.overdue} overdue</span>}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <ChartLegend
            items={[
              { label: 'Renewed', color: CHART_COLORS.good },
              { label: 'Pending', color: CHART_COLORS.warn },
              { label: 'Overdue', color: CHART_COLORS.bad },
            ]}
          />
        </>
      )}
    </CardShell>
  );
}

function CharterCard({
  scope,
  scopeName,
  counts,
  bars,
  onDrill,
}: {
  scope: string | null;
  scopeName: string;
  counts: CharterCounts;
  bars: ReturnType<typeof charterByDistrict>;
  onDrill: OnDrill;
}) {
  const rows: ExRow[] = scope
    ? [
        {
          label: scopeName,
          segments: charterSegments(counts),
          exception: charterException(counts),
          clear: `${counts.chartered} ok`,
        },
      ]
    : bars.map((b) => ({
        label: b.name,
        segments: charterSegments(b),
        exception: charterException(b),
        clear: `${b.chartered} ok`,
        onClick: () =>
          onDrill({
            district: b.districtId,
            filter: b.overdue > 0 ? 'recharterOverdue' : 'recharterDue60',
          }),
      }));

  return (
    <CardShell
      title="Charter renewal"
      link={{ label: 'Unit Health', onClick: () => onDrill() }}
    >
      <ExceptionBars rows={rows} emptyNote="No recharter report imported yet." />
      <SummaryCounts
        items={[
          {
            n: counts.overdue,
            label: 'overdue',
            tone: counts.overdue ? 'bad' : 'mute',
            onClick: counts.overdue ? () => onDrill({ filter: 'recharterOverdue' }) : undefined,
          },
          {
            n: counts.soon,
            label: 'due ≤ 60 days',
            tone: counts.soon ? 'warn' : 'mute',
            onClick: counts.soon ? () => onDrill({ filter: 'recharterDue60' }) : undefined,
          },
          { n: counts.chartered, label: 'chartered', tone: 'mute' },
        ]}
      />
    </CardShell>
  );
}

function charterSegments(c: { chartered: number; soon: number; overdue: number }) {
  return [
    { label: 'chartered', value: c.chartered, color: CHART_COLORS.goodSoft },
    { label: 'due soon', value: c.soon, color: CHART_COLORS.warn },
    { label: 'overdue', value: c.overdue, color: CHART_COLORS.bad },
  ];
}

function paceException(p: AdvancementPace): ExRow['exception'] {
  if (p.stalled > 0) return { text: `${p.stalled} stalled`, tone: 'bad' };
  if (p.behind > 0) return { text: `${p.behind} behind`, tone: 'warn' };
  return null;
}

function AdvancementCard({
  scope,
  scopeName,
  ytd,
  pace,
  bars,
  onDrill,
}: {
  scope: string | null;
  scopeName: string;
  ytd: { eagleYtd: number; scoutsBsaRanksYtd: number; cubRanksYtd: number };
  pace: AdvancementPace;
  bars: ReturnType<typeof advancementPaceByDistrict>;
  onDrill: OnDrill;
}) {
  const rows: ExRow[] = scope
    ? [
        {
          label: scopeName,
          segments: paceSegments(pace),
          exception: paceException(pace),
          clear: `${pace.onTrack} on track`,
        },
      ]
    : bars.map((b) => ({
        label: b.name,
        segments: paceSegments(b),
        exception: paceException(b),
        clear: `${b.onTrack} on track`,
        onClick: () =>
          onDrill({ district: b.districtId, filter: b.stalled > 0 ? 'stalled' : undefined }),
      }));

  return (
    <CardShell title="Advancement" sub="Year to date">
      <div className="dash-statrow">
        <Stat n={ytd.eagleYtd} label="Eagle" />
        <Stat n={ytd.scoutsBsaRanksYtd} label="Scouts BSA ranks" />
        <Stat n={ytd.cubRanksYtd} label="Cub advancement" />
      </div>
      <p className="dash-mini-head">Pace {scope ? '' : 'by district'} · Packs &amp; Troops</p>
      <ExceptionBars rows={rows} emptyNote="No advancement snapshots imported yet." />
    </CardShell>
  );
}

function paceSegments(p: AdvancementPace) {
  return [
    { label: 'on track', value: p.onTrack, color: CHART_COLORS.goodSoft },
    { label: 'behind', value: p.behind, color: CHART_COLORS.warn },
    { label: 'stalled', value: p.stalled, color: CHART_COLORS.bad },
    { label: 'not rated', value: p.notRated, color: CHART_COLORS.neutral },
  ];
}

function CommitteeCard({
  scope,
  committees,
  flagPct,
  onDrill,
}: {
  scope: string | null;
  committees: ReturnType<typeof committeeHealth>;
  flagPct: number;
  onDrill: (d?: string) => void;
}) {
  const shown = committees.flagged.slice(0, 5);
  const more = committees.flagged.length - shown.length;

  return (
    <CardShell
      title="Committee health"
      link={{ label: 'Org Chart', onClick: () => onDrill() }}
    >
      <div className="dash-bignum">
        <strong className={committees.pct < flagPct ? 'is-bad' : undefined}>{committees.pct}%</strong>
        <span>
          {committees.filled} of {committees.total} committee seats filled
          {scope ? '' : ' · all districts'}
        </span>
      </div>
      {shown.length > 0 ? (
        <ul className="dash-gaplist">
          {shown.map((c) => (
            <li key={c.key}>
              <button className="dash-gap" onClick={() => onDrill(c.districtId)}>
                <span className="dash-gap-dot" />
                <span className="dash-gap-name">
                  {scope ? c.committeeName : `${c.districtName} ${c.committeeName}`}
                </span>
                <span className="dash-gap-count">
                  {c.filled}/{c.total}
                </span>
              </button>
            </li>
          ))}
          {more > 0 && (
            <li>
              <button className="dash-gap-more" onClick={() => onDrill()}>
                +{more} more gap{more === 1 ? '' : 's'} →
              </button>
            </li>
          )}
        </ul>
      ) : (
        <p className="dash-empty">
          {committees.total === 0
            ? 'No committee positions configured.'
            : `No committee at or below ${flagPct}% filled.`}
        </p>
      )}
    </CardShell>
  );
}

function LeaderCard({
  scope,
  scopeName,
  syt,
  trained,
  weather,
  alerts,
  onDrill,
}: {
  scope: string | null;
  scopeName: string;
  syt: { done: number; total: number; pct: number };
  trained: { done: number; total: number; pct: number };
  weather: { done: number; total: number; pct: number };
  alerts: { sytFlags: number; leadershipGaps: number; weatherFlags: number };
  onDrill: OnDrill;
}) {
  return (
    <CardShell
      title="Leader readiness"
      sub={scope ? scopeName : 'Council-wide'}
      link={{ label: 'Unit Health', onClick: () => onDrill({ view: 'leaders' }) }}
    >
      <div className="dash-donuts">
        <Donut
          label="SYT current"
          value={syt.done}
          total={syt.total}
          size={92}
          rest={CHART_COLORS.bad}
        />
        <Donut label="Trained in position" value={trained.done} total={trained.total} size={92} />
        <Donut
          label="Hazardous Weather"
          value={weather.done}
          total={weather.total}
          size={92}
          rest={CHART_COLORS.bad}
        />
      </div>
      <SummaryCounts
        items={[
          {
            n: alerts.sytFlags,
            label: 'SYT flags',
            tone: alerts.sytFlags ? 'bad' : 'mute',
            onClick: alerts.sytFlags
              ? () => onDrill({ filter: 'sytNotCurrent', view: 'leaders' })
              : undefined,
          },
          {
            n: alerts.weatherFlags,
            label: 'hazardous weather flags',
            tone: alerts.weatherFlags ? 'bad' : 'mute',
            onClick: alerts.weatherFlags
              ? () => onDrill({ filter: 'hazardousWeather', view: 'leaders' })
              : undefined,
          },
          {
            n: alerts.leadershipGaps,
            label: 'leadership gaps',
            tone: alerts.leadershipGaps ? 'warn' : 'mute',
            onClick: alerts.leadershipGaps
              ? () => onDrill({ filter: 'leadershipGap' })
              : undefined,
          },
        ]}
      />
    </CardShell>
  );
}

function MembershipCard({
  scope,
  scopeName,
  series,
}: {
  scope: string | null;
  scopeName: string;
  series: ReturnType<typeof membershipSeries>;
}) {
  const latest = series[series.length - 1];
  const ytd = ytdGrowth(series);

  return (
    <section className="card dash-card">
      <header className="dash-card-head">
        <div>
          <h3>Membership</h3>
          <p className="dash-card-sub">
            {scope ? scopeName : 'Council'} — youth &amp; adults on the recharter roll
          </p>
        </div>
        {ytd != null && (
          <span className={`dash-ytd ${ytd >= 0 ? 'is-up' : 'is-down'}`}>
            {ytd >= 0 ? '↑' : '↓'} {ytd >= 0 ? '+' : ''}
            {ytd.toFixed(1)}% YTD
          </span>
        )}
      </header>

      {latest ? (
        <div className="dash-member">
          <div className="dash-statrow dash-statrow-lg">
            <Stat n={latest.youth + latest.adults} label="Total scouts" />
            <Stat n={latest.youth} label="Youth" />
            <Stat n={latest.adults} label="Adults" />
          </div>
          <div className="dash-member-chart">
            <LineChart
              yLabel="Members"
              series={[
                {
                  label: 'Youth',
                  color: CHART_COLORS.youth,
                  points: series.map((p) => ({ x: fmtMonth(p.month), y: p.youth })),
                },
                {
                  label: 'Adults',
                  color: CHART_COLORS.adults,
                  points: series.map((p) => ({ x: fmtMonth(p.month), y: p.adults })),
                },
              ]}
              emptyNote="One month recorded — the trend line starts after the next recharter import."
            />
          </div>
        </div>
      ) : (
        <p className="dash-empty">
          No membership history yet — it accumulates from each recharter import.
        </p>
      )}
    </section>
  );
}

// ================================================================
// Bits
// ================================================================

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="dash-stat">
      <strong>{n.toLocaleString()}</strong>
      <span>{label}</span>
    </div>
  );
}

type CountTone = 'bad' | 'warn' | 'mute';
interface CountItem {
  n: number;
  label: string;
  tone: CountTone;
  onClick?: () => void;
}
function SummaryCounts({ items }: { items: CountItem[] }) {
  return (
    <div className="dash-summary">
      {items.map((it, i) => {
        const cls = `dash-sc dash-sc-${it.tone}`;
        return (
          <span key={it.label} className="dash-sc-wrap">
            {i > 0 && <span className="dash-sc-dot">·</span>}
            {it.onClick ? (
              <button className={`${cls} dash-sc-click`} onClick={it.onClick}>
                <b>{it.n}</b> {it.label}
              </button>
            ) : (
              <span className={cls}>
                <b>{it.n}</b> {it.label}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

function AlertChip({
  n,
  label,
  tone,
  icon,
  onClick,
}: {
  n: number;
  label: ReactNode;
  tone: 'warn' | 'bad';
  icon: ReactNode;
  onClick: () => void;
}) {
  const t = n === 0 ? 'ok' : tone;
  return (
    <button className={`dash-alert-chip dash-alert-${t}`} onClick={onClick}>
      <span className="dash-alert-icon" aria-hidden>
        {icon}
      </span>
      <span className="dash-alert-body">
        <b>{n}</b>
        <span className="dash-alert-label">{label}</span>
      </span>
    </button>
  );
}

function ytdGrowth(series: ReturnType<typeof membershipSeries>): number | null {
  if (series.length < 2) return null;
  const year = new Date().getFullYear();
  const base =
    series.find((p) => p.month.startsWith(String(year))) ?? series[0];
  const latest = series[series.length - 1];
  const baseTotal = base.youth + base.adults;
  if (base === latest || baseTotal === 0) return null;
  return ((latest.youth + latest.adults - baseTotal) / baseTotal) * 100;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTHS[(m || 1) - 1]} '${String(y).slice(2)}`;
}

const ICONS = {
  alert: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6M12 16.5v.5" strokeLinecap="round" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" strokeLinecap="round" />
      <path d="M16 6.5a3 3 0 0 1 0 5.8M17 14c2.5.4 4 2.3 4 5" strokeLinecap="round" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" strokeLinejoin="round" />
    </svg>
  ),
};
